// noinspection JSUnusedLocalSymbols

import { Subject } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { finalize, map, take } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ToastService } from '../../../services/toast-service';
import { ChoosePasswordRequest } from '../../../models/users/requests/choose-password-request';
import { BaseAuthViewModel } from '../../../models/base/base-auth-view-model';
import {
  InternalUserAuthFlow,
  InternalUserAuthFlowSubtext,
  InternalUserAuthFlowTitle
} from '../../../models/users/enum/internal-user-auth-flow.enum';
import { AuthCodeRequest } from '../../../models/users/requests/auth-code-request';
import { CacheService } from '../../../services/cache-service';
import { ScreenService } from '../../../services/screen-service.service';

@Injectable({ providedIn: 'root' })
export class InternalUserAuthViewModel extends BaseAuthViewModel {
  private screenService = inject(ScreenService);

  constructor(public router: Router, public toastService: ToastService, public cacheService: CacheService) {
    super(router, toastService, cacheService);
    this.init();
  }

  // Observables
  public nextAuthFlow: Subject<InternalUserAuthFlow> = new Subject<InternalUserAuthFlow>();

  public authFlowMap = new Map<number, InternalUserAuthFlow>();

  public readonly authFlowTitle$ = this.authFlow$.pipe(map(flow => InternalUserAuthFlowTitle(flow)));
  public readonly authFlowSubtext$ = this.authFlow$.pipe(map(flow => InternalUserAuthFlowSubtext(flow)));

  public showBackButton$ = this.authFlow$.pipe(
    map(flow => {
      return (
        flow !== InternalUserAuthFlow.SignIn &&
        flow !== InternalUserAuthFlow.InternalUserSetupMfa &&
        flow !== InternalUserAuthFlow.InternalVerifyCode
      );
    })
  );

  public isMobile$ = this.screenService.isMobile$;

  // Auth Flows

  goBack(): void {
    this.authFlow$.once(flow => {
      switch (flow) {
        case InternalUserAuthFlow.ResetPassword: {
          this.router.navigate(['auth/forgot-password']).then();
          break;
        }
        case InternalUserAuthFlow.ForgotPassword: {
          this.router.navigate(['auth/sign-in']).then();
          break;
        }
        case InternalUserAuthFlow.InternalUserChoosePassword: {
          this.router.navigate(['auth/sign-in']).then();
          break;
        }
      }
    });
  }

  public generateAuthFlowMap() {
    this.authFlowMap?.set(0, InternalUserAuthFlow.SignIn);
    this.authFlowMap?.set(1, InternalUserAuthFlow.ForgotPassword);
    this.authFlowMap?.set(2, InternalUserAuthFlow.ResetPassword);
    this.authFlowMap?.set(3, InternalUserAuthFlow.SetNewPassword);
    this.authFlowMap?.set(4, InternalUserAuthFlow.InternalUserChoosePassword);
    this.authFlowMap?.set(5, InternalUserAuthFlow.InternalUserSetupMfa);
    this.authFlowMap?.set(6, InternalUserAuthFlow.InternalVerifyCode);
  }

  public setNewInternalUserPassword(req: ChoosePasswordRequest): void {
    const loadingMessage = $localize`Signing in`;
    this._loadingOpts.addRequest(loadingMessage);
    this.userDomainModel
      .setNewInternalUserPassword(req)
      .pipe(
        take(1),
        finalize(() => this._loadingOpts.removeRequest(loadingMessage))
      )
      .subscribe({
        next: res => {
          this.router.navigate(['/auth/internal-user-auth-setup'], { replaceUrl: true }).then(() => {});
        },
        error: error => {
          this.toastService.publishErrorMessage('Error', error);
        }
      });
  }

  public verifyMfaCode(req: AuthCodeRequest, setup: boolean): void {
    const loadingMessage = $localize`Verifying`;
    this._loadingOpts.addRequest(loadingMessage);
    this.userDomainModel
      .verifyMfaCode(req)
      .pipe(
        take(1),
        finalize(() => this._loadingOpts.removeRequest(loadingMessage))
      )
      .subscribe({
        next: res => {
          if (setup) {
            this.toastService.publishSuccessMessage($localize`MFA Configured!`, $localize`Please sign in again.`);
            this.router.navigate(['/auth/sign-in']).then(() => {});
          } else {
            this.router.navigate(['/dashboard']).then();
          }
        },
        error: error => {
          if (error.code === 400) {
            this.toastService.publishErrorMessage($localize`The verification code you have entered is invalid.`);
          } else {
            this.toastService.publishErrorMessage($localize`Error`, error.title);
          }
        }
      });
  }
}
